(() => {
  const ROOT_SELECTOR = '[data-ln-cart-bundle-upgrade]';
  const BUTTON_SELECTOR = '[data-ln-cart-upgrade-submit]';
  const SOURCE = 'ln-cart-bundle-upgrade';
  let requestPending = false;

  const analyticsPayload = (root, extra = {}) => ({
    surface: 'cart_page',
    mode: 'upgrade',
    bundle_id: positiveNumber(root?.dataset.lnBundleId) || undefined,
    bundle_handle: root?.dataset.lnBundleHandle,
    bundle_title: root?.dataset.bundleTitle,
    variant_id: positiveNumber(root?.dataset.bundleVariantId) || undefined,
    value: Number(root?.dataset.lnValue) || undefined,
    currency: root?.dataset.lnCurrency,
    replace_count: Number(root?.dataset.lnReplaceCount) || undefined,
    upgrade_delta: Number(root?.dataset.lnUpgradeDelta) || 0,
    cart_value_before: Number(root?.dataset.lnCartValueBefore) || 0,
    projected_total: Number(root?.dataset.lnProjectedTotal) || 0,
    unlocks_free_shipping: root?.dataset.lnUnlocksFreeShipping === 'true',
    component_one: root?.dataset.lnComponentOne,
    component_two: root?.dataset.lnComponentTwo,
    ...extra,
  });

  const trackAnalytics = (name, root, extra = {}) => {
    try {
      window.LashNovaAnalytics?.track?.(name, analyticsPayload(root, extra));
    } catch (_error) {
      // Analytics must never interrupt the cart transaction.
    }
  };

  const analyticsErrorCode = (error) => {
    try {
      return window.LashNovaAnalytics?.classifyError?.(error) || 'cart_update_failed';
    } catch (_analyticsError) {
      return 'cart_update_failed';
    }
  };

  const positiveNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : 0;
  };

  const getSectionTargets = () => {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    return [
      { id: 'main-cart-items', section: items?.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: footer?.dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
    ].filter((target) => target.section);
  };

  const renderSections = (cartState, targets) => {
    let rendered = 0;

    targets.forEach((target) => {
      const targetRoot = document.getElementById(target.id);
      const sectionHTML = cartState?.sections?.[target.section];
      if (!targetRoot || typeof sectionHTML !== 'string') return;

      const sourceDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const source = sourceDocument.querySelector(target.selector);
      const destination = targetRoot.querySelector(target.selector) || targetRoot;
      if (!source || !destination) return;

      destination.innerHTML = source.innerHTML;
      rendered += 1;
    });

    return rendered === targets.length;
  };

  const publishCartUpdate = (cartData) => {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    try {
      Promise.resolve(
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData,
        })
      ).catch((error) => console.error('Cart update subscriber failed:', error));
    } catch (error) {
      console.error('Cart update subscriber failed:', error);
    }
  };

  const announce = (message) => {
    const liveRegion = document.getElementById('cart-live-region-text');
    if (!liveRegion) return;
    liveRegion.textContent = message;
    liveRegion.setAttribute('aria-hidden', 'false');
  };

  const setBusy = (root, button, busy) => {
    root?.classList.toggle('is-loading', busy);
    if (button) {
      button.disabled = busy;
      button.setAttribute('aria-disabled', String(busy));
      button.classList.toggle('loading', busy);
      button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
    }
  };

  const showError = (message) => {
    const currentRoot = document.querySelector(ROOT_SELECTOR);
    const inlineError = currentRoot?.querySelector('[data-ln-cart-upgrade-error]');
    if (inlineError) {
      inlineError.textContent = message;
      inlineError.hidden = false;
      return;
    }

    const cartError = document.getElementById('cart-errors');
    if (cartError) cartError.textContent = message;
  };

  const parseProperties = (value) => {
    try {
      const properties = JSON.parse(value || '{}');
      return properties === null ? {} : typeof properties === 'object' ? properties : null;
    } catch (_error) { return null; }
  };

  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest(BUTTON_SELECTOR) : null;
    if (!button) return;
    event.preventDefault();
    const root = button.closest(ROOT_SELECTOR);
    const transactions = window.LashNovaCartTransactions;
    if (!root || requestPending || root.classList.contains('is-loading')) return;
    if (!transactions || transactions.isBusy()) {
      showError(transactions?.text('busy') || window.cartStrings?.error || '');
      return;
    }

    const bundleVariantId = positiveNumber(root.dataset.bundleVariantId);
    const sourceDescriptors = Array.from(root.querySelectorAll('[data-ln-cart-upgrade-line]')).map((line) => ({
      key: line.dataset.key || '',
      variantId: positiveNumber(line.dataset.variantId),
      properties: parseProperties(line.dataset.properties),
      sellingPlanId: Number(line.dataset.sellingPlanId) || 0,
    }));
    if (!bundleVariantId || !sourceDescriptors.length || sourceDescriptors.some((line) => !line.variantId || line.properties === null)) {
      showError(transactions.text('invalid_state'));
      return;
    }

    trackAnalytics('ln_bundle_offer_click', root);
    requestPending = true;
    setBusy(root, button, true);
    root.querySelector('[data-ln-cart-upgrade-error]')?.setAttribute('hidden', '');
    const targets = getSectionTargets();
    try {
      const outcome = await transactions.executeBundleUpgrade({
        bundleVariantId,
        sourceDescriptors,
        sections: targets.map((target) => target.section),
        sectionsUrl: window.location.pathname,
        onResult: (result) => {
          if (!result.verified || !result.cart) return;
          if (!renderSections(result.cart, targets)) {
            window.location.assign(window.routes.cart_url);
            return;
          }
          publishCartUpdate(result.cart);
          announce(transactions.text(result.messageKey));
        },
      });
      if (outcome.status === 'upgraded') {
        trackAnalytics('ln_bundle_upgrade_success', root, { replaced_quantity: outcome.sourceCount, final_state: 'bundle_replaced_singles' });
      } else {
        trackAnalytics('ln_bundle_upgrade_error', root, {
          error_code: outcome.status === 'busy' ? 'cart_busy' : analyticsErrorCode(outcome.error),
          final_state: outcome.status === 'review' ? 'review_required' : 'verified_singles_not_replaced',
        });
        showError(transactions.text(outcome.messageKey));
      }
      if (!outcome.verified && outcome.status !== 'busy') window.location.assign(window.routes.cart_url);
    } catch (_error) {
      showError(transactions.text('review_needed'));
      window.location.assign(window.routes.cart_url);
    } finally {
      requestPending = false;
      if (root.isConnected) setBusy(root, button, false);
    }
  });
})();
